import { useEffect, useMemo, useRef, useState } from 'react'
import { emojiFromEmotion } from '../../../components/DeviceScreenPreview'
import { normalizeEmotion } from '../../../utils/emotion'
import { useI18n } from '../../../utils/i18n'
import { safeStorageGet, safeStorageSet } from '../../../utils/storage'
import { CloudChatCard, ConnectionCard, EyesPreviewCard } from './cloud-test/CloudTestPanels'
import { buildWsUrlCandidates, formatCloudMessageTime } from './cloud-test/cloudTestWs'

const IS_MOBILE = window.matchMedia?.('(max-width: 640px)')?.matches ?? false

export default function CloudTest() {
  const { tx, lang } = useI18n()

  const [connecting, setConnecting] = useState(false)
  const [wsConnected, setWsConnected] = useState(false)
  const [retryIn, setRetryIn] = useState(0)
  const [wsUrl, setWsUrl] = useState('')
  const [wsSessionId, setWsSessionId] = useState('')

  const [deviceOnline, setDeviceOnline] = useState(false)
  const [deviceId, setDeviceId] = useState('-')

  const [targetDeviceId, setTargetDeviceId] = useState(() => {
    const saved = safeStorageGet('cloudtest.deviceId') || ''
    if (saved) return saved
    try {
      const qp = new URLSearchParams(window.location.search)
      return String(qp.get('device-id') || '').trim()
    } catch {
      return ''
    }
  })

  const [chatItems, setChatItems] = useState([])
  const [inputText, setInputText] = useState('')
  const [sending, setSending] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [streamText, setStreamText] = useState('')
  const streamRef = useRef('')

  const [emotion, setEmotion] = useState('neutral')
  const [emotionEmoji, setEmotionEmoji] = useState('😶')
  const [eyeTheme, setEyeTheme] = useState(() => safeStorageGet('cloudtest.eyeTheme') || 'robo')
  const [eyeColor, setEyeColor] = useState(() => safeStorageGet('cloudtest.eyeColor') || '#FFD233')
  const [previewPoke, setPreviewPoke] = useState(0)

  const wsRef = useRef(null)
  const chatEndRef = useRef(null)
  const reconnectTimerRef = useRef(null)
  const countdownTimerRef = useRef(null)
  const pingTimerRef = useRef(null)
  const wsCandidateIndexRef = useRef(0)
  const manualCloseRef = useRef(false)
  const openedRef = useRef(false)

  const effectiveDeviceId = useMemo(() => String(targetDeviceId || '').trim() || 'public-test', [targetDeviceId])

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: IS_MOBILE ? 'auto' : 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [chatItems, streamText, isTyping])

  useEffect(() => {
    safeStorageSet('cloudtest.deviceId', targetDeviceId || '')
  }, [targetDeviceId])

  useEffect(() => {
    safeStorageSet('cloudtest.eyeTheme', eyeTheme || 'robo')
  }, [eyeTheme])

  useEffect(() => {
    safeStorageSet('cloudtest.eyeColor', eyeColor || '#FFD233')
  }, [eyeColor])

  const addSys = (text) => {
    setChatItems((prev) => [...prev.slice(-199), { dir: 'sys', text, time: formatCloudMessageTime() }])
  }

  const cleanupTimers = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current)
      pingTimerRef.current = null
    }
  }

  const resetStream = () => {
    streamRef.current = ''
    setStreamText('')
    setIsTyping(false)
  }

  const handleMessage = (data) => {
    const type = data?.type

    if (type === 'hello') {
      setWsSessionId(String(data.session_id || '').substring(0, 8))
      return
    }

    if (type === 'device_status') {
      setDeviceOnline(data.status === 'online')
      setDeviceId(data.device_id || '-')
      return
    }

    // 情绪广播（来自云端规则/LLM）
    if (type === 'llm') {
      if (data.emotion) {
        const e = normalizeEmotion(data.emotion)
        setEmotion(e)
        setEmotionEmoji((prev) => (data.text ? data.text : prev || emojiFromEmotion(e)))
      } else if (data.text) {
        setEmotionEmoji(String(data.text || '').trim())
      }
      return
    }

    if (type === 'conversation') {
      const role = data.role
      const text = String(data.text || '')
      const time = formatCloudMessageTime(data.timestamp)

      if (role === 'user') {
        resetStream()
        setChatItems((prev) => [...prev.slice(-199), { dir: 'out', text, time }])
        return
      }

      if (role === 'assistant') {
        const streamState = data?.meta?.stream_state
        if (streamState === 'start') {
          streamRef.current = ''
          setStreamText('')
          setIsTyping(true)
          return
        }
        if (streamState === 'delta') {
          streamRef.current += text
          setStreamText(streamRef.current)
          setIsTyping(true)
          return
        }
        if (streamState === 'stop' || streamState === 'error') {
          const finalText = String(text || streamRef.current || '').trim()
          if (finalText) setChatItems((prev) => [...prev.slice(-199), { dir: 'in', text: finalText, time }])
          resetStream()
          return
        }

        resetStream()
        setChatItems((prev) => [...prev.slice(-199), { dir: 'in', text, time }])
        return
      }
      return
    }

    if (type === 'error') {
      const msg = String(data.error || 'unknown error')
      addSys(tx({ zh: '错误：{msg}', en: 'Error: {msg}' }, { msg }))
      return
    }
  }

  const connect = (overrideDeviceId) => {
    const id = String(overrideDeviceId ?? effectiveDeviceId).trim() || 'public-test'
    cleanupTimers()

    openedRef.current = false
    manualCloseRef.current = false

    if (wsRef.current) {
      manualCloseRef.current = true
      wsRef.current.close()
      wsRef.current = null
    }

    setConnecting(true)
    setRetryIn(0)
    setWsSessionId('')
    setWsConnected(false)

    try {
      const candidates = buildWsUrlCandidates(id)
      const idx = wsCandidateIndexRef.current || 0
      const pick = candidates[Math.min(idx, candidates.length - 1)]
      setWsUrl(pick)

      const ws = new WebSocket(pick)
      wsRef.current = ws

      ws.onopen = () => {
        openedRef.current = true
        setWsConnected(true)
        setConnecting(false)
        setRetryIn(0)
        wsCandidateIndexRef.current = 0

        try {
          ws.send(
            JSON.stringify({
              type: 'hello',
              version: 1,
              client_id: 'web-chat',
              transport: 'websocket',
              audio_params: {
                format: 'opus',
                sample_rate: 16000,
                channels: 1,
                frame_duration: 60,
              },
            }),
          )
        } catch {}

        // keepalive：避免空闲超时导致断开
        pingTimerRef.current = setInterval(() => {
          try {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'ping' }))
            }
          } catch {}
        }, 15000)
      }

      ws.onclose = () => {
        const wasManual = manualCloseRef.current
        manualCloseRef.current = false
        setWsConnected(false)
        setConnecting(false)
        resetStream()
        if (pingTimerRef.current) {
          clearInterval(pingTimerRef.current)
          pingTimerRef.current = null
        }

        if (wasManual) return

        // 如果连接从未成功建立：尝试切换到下一个 WS 入口
        try {
          const nextCandidates = buildWsUrlCandidates(id)
          if (!openedRef.current && wsCandidateIndexRef.current < nextCandidates.length - 1) {
            wsCandidateIndexRef.current += 1
            reconnectTimerRef.current = setTimeout(() => connect(id), 300)
            return
          }
        } catch {}

        let remain = 5
        setRetryIn(remain)
        countdownTimerRef.current = setInterval(() => {
          remain -= 1
          setRetryIn(remain)
          if (remain <= 0) {
            clearInterval(countdownTimerRef.current)
            countdownTimerRef.current = null
          }
        }, 1000)
        reconnectTimerRef.current = setTimeout(() => connect(id), 5000)
      }

      ws.onerror = () => {
        setConnecting(false)
      }

      ws.onmessage = (e) => {
        try {
          const obj = JSON.parse(e.data)
          handleMessage(obj)
        } catch {}
      }
    } catch (e) {
      setConnecting(false)
      addSys(tx({ zh: '连接失败：{err}', en: 'Connect failed: {err}' }, { err: String(e?.message || e) }))
      reconnectTimerRef.current = setTimeout(() => connect(id), 5000)
    }
  }

  const disconnect = () => {
    cleanupTimers()
    if (wsRef.current) {
      manualCloseRef.current = true
      wsRef.current.close()
      wsRef.current = null
    }
    setWsConnected(false)
    setConnecting(false)
    setRetryIn(0)
    setWsSessionId('')
    resetStream()
  }

  const clearChat = () => {
    setChatItems([])
    resetStream()
  }

  const sendChat = (overrideText) => {
    const text = String(overrideText ?? inputText).trim()
    if (!text) return

    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      addSys(tx({ zh: '未连接云端，无法发送', en: 'Not connected; cannot send' }))
      return
    }

    setSending(true)
    setInputText('')
    try {
      ws.send(JSON.stringify({ type: 'chat_cloud', text, mode: 'manual', lang }))
    } catch (e) {
      addSys(tx({ zh: '发送失败：{err}', en: 'Send failed: {err}' }, { err: String(e?.message || e) }))
    } finally {
      setSending(false)
    }
  }

  useEffect(() => {
    // 自动连接（默认 public-test）
    connect(effectiveDeviceId)
    return () => {
      cleanupTimers()
      if (wsRef.current) {
        manualCloseRef.current = true
        wsRef.current.close()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-4">
      <ConnectionCard
        tx={tx}
        targetDeviceId={targetDeviceId}
        setTargetDeviceId={setTargetDeviceId}
        wsConnected={wsConnected}
        connecting={connecting}
        retryIn={retryIn}
        wsSessionId={wsSessionId}
        wsUrl={wsUrl}
        deviceOnline={deviceOnline}
        onConnect={() => connect(targetDeviceId)}
        onDisconnect={disconnect}
        onClear={clearChat}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <CloudChatCard
          tx={tx}
          wsConnected={wsConnected}
          sending={sending}
          inputText={inputText}
          setInputText={setInputText}
          chatItems={chatItems}
          streamText={streamText}
          isTyping={isTyping}
          chatEndRef={chatEndRef}
          onSend={sendChat}
        />

        <div className="flex flex-col gap-4 min-h-[70vh]">
          <EyesPreviewCard
            tx={tx}
            deviceId={deviceId}
            eyeTheme={eyeTheme}
            setEyeTheme={setEyeTheme}
            eyeColor={eyeColor}
            setEyeColor={setEyeColor}
            emotion={emotion}
            emotionEmoji={emotionEmoji}
            previewPoke={previewPoke}
            setPreviewPoke={setPreviewPoke}
          />
        </div>
      </div>
    </div>
  )
}
